/// <amd-module name="Build/Common/Library/InstallAppService" />
import * as Build from "Build/Client/Contracts/Build";
import { openLinkSecurely } from "Build/Common/Library/Navigation";

import { IVssService, Services, VssService } from "VSS/Platform/Context";
import { IVssContributionService } from "VSS/Platform/Contribution";
import { IVssTelemetryService } from "VSS/Platform/Telemetry";
import { IVssHistoryService } from "VSS/Platform/History";

export interface IInstallAppService extends IVssService {
    installApp(): void;
    installAppForEdit(pipelineId: number, nonce: string, installAppUrlRoot: string, repository: Build.BuildRepository): void;
    installAppForWizard(sourceProvider: string, connectionId: string, installAppUrlRoot: string, repository: Build.SourceRepository): void;
}

// Explanation:
// Unfortunately, when we redirect the user to external source providers, such as GitHub, 
// there isn't a clean way for the user to get back to the editor with everything correctly selected.
// So, we are putting some info in the users attributes that AEX will look for when the provider
// redirects to our setup url after installing the app. We need enough information in the attributes 
// to get back here.
export class InstallAppService extends VssService implements IInstallAppService {
    private static _redirectUrl: string = "https://go.microsoft.com/fwlink/?linkid=874367"; // Debug App: "https://github.com/apps/vststestapp/installations/new"

    public installApp(): void {
        // Save the continuation information and then redirect to the app install page
        const contributionService = this.pageContext.getService<IVssContributionService>("IVssContributionService");
        const telemetrySession = this._getTelemetrySession();
        contributionService.getDataAsync<boolean>("ms.vss-build-web.wizard-continuation-data-provider", 
            {
                telemetrySession: telemetrySession,
            }, true)
        .then(() => {
            // Whether the call succeeded or not, we want to continue to take the user to the redirectUrl
            this._logTelemetry("installApp", "CreatePipeline", telemetrySession, "");
            openLinkSecurely(InstallAppService._redirectUrl, false); // TODO open in another tab?
        });
    }

    public installAppForWizard(sourceProvider: string, connectionId: string, installAppUrlRoot: string, repository: Build.SourceRepository): void {
        // Save the continuation information and then redirect to the app install page
        let redirectUrl: string = InstallAppService._redirectUrl;
        if (repository.properties && repository.properties["ownerId"]) {
            const ownerId = repository.properties["ownerId"];
            const repoId = repository.properties["externalId"];
            const queryChar = installAppUrlRoot.indexOf("?") > 0 ? "&" : "?";
            redirectUrl = installAppUrlRoot + queryChar + "suggested_target_id=" + ownerId + "&repository_ids[]=" + repoId;
        }

        const telemetrySession = this._getTelemetrySession();
        const contributionService = this.pageContext.getService<IVssContributionService>("IVssContributionService");
        contributionService.getDataAsync<boolean>("ms.vss-build-web.wizard-continuation-data-provider", 
            {
                sourceProvider: sourceProvider,
                repositoryId: repository.id,
                repositoryName: repository.name,
                connectionId: connectionId,
                telemetrySession: telemetrySession,
            },
            true)
        .then(() => {
            // Whether the call succeeded or not, we want to continue to take the user to the redirectUrl
            this._logTelemetry("installAppForRepository", "CreatePipeline", telemetrySession, repository.id);
            openLinkSecurely(redirectUrl, false);
        });
    }

    public installAppForEdit(pipelineId: number, nonce: string, installAppUrlRoot: string, repository: Build.BuildRepository): void {
        // Save the continuation information and then redirect to the marketplace app install page
        let redirectUrl: string = InstallAppService._redirectUrl;
        if (repository.properties && repository.properties["ownerId"]) {
            const ownerId = repository.properties["ownerId"];
            const repoId = repository.properties["externalId"];
            const queryChar = installAppUrlRoot.indexOf("?") > 0 ? "&" : "?";
            redirectUrl = installAppUrlRoot + queryChar + "suggested_target_id=" + ownerId + "&repository_ids[]=" + repoId;
        }

        const contributionService = this.pageContext.getService<IVssContributionService>("IVssContributionService");
        contributionService.getDataAsync<boolean>("ms.vss-build-web.pipeline-edit-continuation-data-provider", {
            pipelineId: pipelineId.toString(),
            nonce: nonce,
            repositoryId: repository.id
        }, true).then(() => {
            this._logTelemetry("installAppForRepository", "EditPipeline", repository.id);
            openLinkSecurely(redirectUrl, false);
        });
    }

    private _getTelemetrySession(): string {
        const historyService = this.pageContext.getService<IVssHistoryService>("IVssHistoryService");
        const state = historyService.getState();
        return state.telemetrySession;
    }

    private _logTelemetry(method: string, layer: string, telemetrySession: string, repository?: string) {
        const telemetryService = this.pageContext.getService<IVssTelemetryService>("IVssTelemetryService");
        telemetryService.publishEvent(
            "Build",
            layer,
            {
                GitHubApp: method,
                GitHubAppRepo: repository,
                action: "InstallGitHubApp",
                telemetrySession: telemetrySession || "unknown",
            });
    }
}

Services.add("IInstallAppService", { serviceFactory: InstallAppService });
